import { ViewController } from '@estee/elc-service';
import { emit, events } from '@estee/elc-service-bus';
import { observable, computed, action } from 'mobx';
import { AVERAGE_RATING } from '~constants/PowerReviews';
import { computeRoundedValueFor } from '~utils/ComputeRoundedValueFor';
import { IProduct } from '~interfaces/IRating';
import { RatingSelectedUseCase } from '../use-cases/RatingSelectedUseCase';
import { DEFAULT_NUMBER_OF_STARS, REVIEW_CATEGORY } from '../constants/ProductReview';
import { RatingsLoadedUseCase } from '../use-cases/RatingsLoadedUseCase';
import { ProductRepository } from '../repositories/ProductRepository';
import { NO_VALUE } from '../constants/NoValue';
import { RatingsRepository } from '../repositories/RatingsRepository';
import { Review } from '../domain/entities/Review';
import { ISocialProofReviewConfig } from '~exported/product-rating/ProductRating';
import { generateRandomIntegerUpTo } from '~utils/GenerateRandomIntegerUpTo';

export interface IProductRatingViewControllerInitializeProps {
    productId: string;
    numberOfStars: number;
    productRating?: number;
    reviewsNumber?: number;
    product: IProduct;
    socialProofReviews: ISocialProofReviewConfig;
}

interface IProductRating {
    averageRating: number;
    reviewCount: number;
}

export class ProductRatingViewController extends ViewController {
    @observable public selectedRatingIndex: number = -1;
    @observable public productRepository: ProductRepository;
    @observable public productId: string;
    @observable private numberOfStars: number = DEFAULT_NUMBER_OF_STARS;
    @observable public numberOfReviews: number;
    @observable private ratingsRepository: RatingsRepository;
    @observable private hasLoaded: boolean = false;
    @observable private productItem: IProduct;
    private defaultRating: IProductRating;
    @observable private reviews: Review[];
    @observable private socialProofReviewConfig: ISocialProofReviewConfig;

    constructor(productRepository: ProductRepository, ratingsRepository: RatingsRepository) {
        super();
        this.productRepository = productRepository;
        this.ratingsRepository = ratingsRepository;
    }

    @computed
    public get data() {
        return {
            selectedRatingIndex: this.selectedRatingIndex,
            percentageRating: this.percentageRating,
            onSelectRating: this.onSelectRating,
            onReadReviewsClick: this.onReadReviewsClick,
            onWriteReviewClick: this.onWriteReviewClick,
            numberOfReviews: this.rating?.reviewCount,
            averageRating: this.rating?.averageRating,
            loaded: this.hasLoaded,
            reviews: this.reviews,
            filteredSocialProofReview: this.filteredSocialProofReview
        };
    }

    @computed
    public get rating(): IProductRating {
        return (
            this.ratingsRepository.productRatings[this.productId] ||
            this.productLevelReviews ||
            this.defaultRating
        );
    }

    @computed
    public get product() {
        return this.productItem || this.productRepository.getProductById(this.productId);
    }

    @action
    public loaded = async (props: IProductRatingViewControllerInitializeProps) => {
        this.isLoading = true;

        this.defaultRating = {
            reviewCount: props.reviewsNumber || 0,
            averageRating: computeRoundedValueFor(props.productRating || 0)
        };

        this.productItem = props.product || null;

        const { numberOfStars, productId } = props;
        this.numberOfStars = numberOfStars;
        this.productId = productId;

        if (!props.product) {
            const ratingsLoadedUseCase = new RatingsLoadedUseCase(this.productId);
            await ratingsLoadedUseCase.execute();
        }
        this.ratingsRepository.getProductRating(productId);

        this.socialProofReviewConfig = props.socialProofReviews;
        if (this.socialProofReviewConfig?.enabled) {
            this.reviews = await this.ratingsRepository.getProductReviews(this.productId);
        }

        this.isLoading = false;
        this.hasLoaded = true;
    };

    @computed
    private get percentageRating() {
        const defaultVal = undefined;

        if (this.rating && AVERAGE_RATING in this.rating) {
            const { averageRating } = this.rating;

            return averageRating === NO_VALUE
                ? defaultVal
                : (averageRating * 100) / this.numberOfStars;
        }

        return defaultVal;
    }

    @action
    public onSelectRating = (index: number) => {
        this.selectedRatingIndex = index;

        const ratingSelectedUseCase = new RatingSelectedUseCase(index);
        ratingSelectedUseCase.execute();
    };

    @action
    public onReadReviewsClick = () => {
        emit(events.READ_REVIEWS_LINK_CLICKED, this.getReviewPayload());
    };

    @action
    public onWriteReviewClick = () => {
        emit(events.WRITE_REVIEWS_LINK_CLICKED, this.getReviewPayload());
    };

    @computed
    private get productLevelReviews() {
        if (!this.product) {
            return null;
        }

        return {
            reviewCount: this.product.reviews.numberOfReviews,
            averageRating: this.product.reviews.averageRating
        };
    }

    private getReviewPayload() {
        const { productId, displayName } = this.product;

        return {
            id: productId,
            name: displayName,
            reviewCategory: REVIEW_CATEGORY
        };
    }

    @action
    public setRating = (index: number) => {
        this.numberOfReviews = this.numberOfReviews + 1;
    };

    private getReviewHeadline = (maxCharacters: number) => {
        const { minNumberOfStars } = this.socialProofReviewConfig || {};
        const filteredReviews = this.reviews?.filter(
            ({ rating, headline }) => rating >= minNumberOfStars && headline.length <= maxCharacters
        );

        return filteredReviews?.[generateRandomIntegerUpTo(filteredReviews?.length)]?.headline;
    };

    @computed
    public get filteredSocialProofReview() {
        const { maxNumberOfCharacters } = this.socialProofReviewConfig || {};

        return {
            desktopHeadline: this.getReviewHeadline(maxNumberOfCharacters?.longHeadline) ?? '',
            mobileHeadline: this.getReviewHeadline(maxNumberOfCharacters?.shortHeadline) ?? ''
        };
    }
}
